import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ProjectOverviewLink } from "@/components/ProjectOverviewLink";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Checkbox, TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { Table, type Column } from "@/components/ui/Table";
import { ApiError } from "@/lib/apiClient";
import {
  currentYear,
  shiftYear,
} from "@/lib/auswertungen";
import { getProjectCustomFieldSchemas, listAllOrders, type Order } from "@/lib/projects";
import { HoursUsageCell } from "@/pages/projekte/HoursUsageCell";
import {
  buildLabelIndexFromSchemas,
  computeHoursUsageMeta,
  fetchActualHoursBatch,
  finalizeHoursUsageEntry,
  formatHours,
  getHoursUsageTrafficLight,
  toHoursUsageDisplay,
  type HoursUsageEntry,
  type HoursUsageTrafficLight,
} from "@/pages/projekte/hoursUsage";

interface FestpreisReportRow {
  order: Order;
  entry: HoursUsageEntry;
}

const TRAFFIC_LIGHT_LABELS: Record<HoursUsageTrafficLight, string> = {
  ok: "Im Plan",
  warn: "Kritisch",
  over: "Überschritten",
};

function trafficLightBadgeClass(state: HoursUsageTrafficLight | null): string {
  switch (state) {
    case "warn":
      return "eh-festpreis-traffic-light eh-festpreis-traffic-light--warn";
    case "over":
      return "eh-festpreis-traffic-light eh-festpreis-traffic-light--over";
    case "ok":
    default:
      return "eh-festpreis-traffic-light eh-festpreis-traffic-light--ok";
  }
}

export function FestpreisHoursReport() {
  const [year, setYear] = useState(currentYear);
  const [search, setSearch] = useState("");
  const [onlyAtRisk, setOnlyAtRisk] = useState(false);
  const [onlyWithPlannedHours, setOnlyWithPlannedHours] = useState(false);
  const [rows, setRows] = useState<FestpreisReportRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [orders, schemas] = await Promise.all([
        listAllOrders({
          status: "alle",
          jahr: year,
          include_hero_custom_fields: true,
          include_hero_steps: false,
          include_timesheets: false,
          include_timesheet_numbers: false,
          include_tags: false,
          prepare_workflow: false,
        }),
        getProjectCustomFieldSchemas(),
      ]);
      const labelIndex = buildLabelIndexFromSchemas(schemas);
      const eligibleOrders = orders.filter((order) => computeHoursUsageMeta(order, labelIndex).eligible);

      const actualById = await fetchActualHoursBatch(eligibleOrders);
      const reportRows: FestpreisReportRow[] = eligibleOrders.map((order) => {
        const meta = computeHoursUsageMeta(order, labelIndex);
        const actual = actualById.get(order.id);
        return {
          order,
          entry: finalizeHoursUsageEntry(
            meta,
            actual?.actualHours ?? null,
            Boolean(actual?.error),
          ),
        };
      });

      setRows(reportRows);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Festpreis-Stundenverbrauch konnte nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = [...(rows ?? [])];

    if (onlyWithPlannedHours) {
      list = list.filter(({ entry }) => entry.plannedHours != null && entry.plannedHours > 0);
    }

    if (term) {
      list = list.filter(({ order }) => {
        const haystack = [order.auftragsnummer, order.beschreibung, order.kunde, order.ort]
          .map((value) => String(value || "").toLowerCase())
          .join(" ");
        return haystack.includes(term);
      });
    }

    if (onlyAtRisk) {
      list = list.filter(({ entry }) => {
        const light = getHoursUsageTrafficLight(entry.percentRaw);
        return light === "warn" || light === "over";
      });
    }

    return list.sort((a, b) => {
      const percentA = Number.isFinite(a.entry.percentRaw) ? Number(a.entry.percentRaw) : -1;
      const percentB = Number.isFinite(b.entry.percentRaw) ? Number(b.entry.percentRaw) : -1;
      if (percentA !== percentB) return percentB - percentA;
      return String(a.order.auftragsnummer || "").localeCompare(
        String(b.order.auftragsnummer || ""),
        "de",
      );
    });
  }, [rows, search, onlyAtRisk, onlyWithPlannedHours]);

  const summary = useMemo(() => {
    const base = rows ?? [];
    let ok = 0;
    let warn = 0;
    let over = 0;
    let withPlanned = 0;
    for (const row of base) {
      if (row.entry.plannedHours != null && row.entry.plannedHours > 0) {
        withPlanned += 1;
      }
      const light = getHoursUsageTrafficLight(row.entry.percentRaw);
      if (light === "over") over += 1;
      else if (light === "warn") warn += 1;
      else if (light === "ok") ok += 1;
    }
    return { total: base.length, withPlanned, ok, warn, over };
  }, [rows]);

  const columns = useMemo<Column<FestpreisReportRow>[]>(
    () => [
      {
        key: "ampel",
        header: "Ampel",
        sortable: true,
        sortValue: (row) =>
          Number.isFinite(row.entry.percentRaw) ? Number(row.entry.percentRaw) : -1,
        render: (row) => {
          const display = toHoursUsageDisplay(row.entry);
          const light = display?.trafficLight ?? null;
          return (
            <span className={clsx("inline-flex items-center gap-2", trafficLightBadgeClass(light))}>
              <span className="eh-festpreis-traffic-light__dot" aria-hidden />
              {light ? TRAFFIC_LIGHT_LABELS[light] : "–"}
            </span>
          );
        },
      },
      {
        key: "auftrag",
        header: "Auftrag",
        sortable: true,
        sortValue: (row) => row.order.auftragsnummer || "",
        render: (row) => {
          const parts = [row.order.auftragsnummer, row.order.beschreibung].filter(Boolean);
          const label = parts.join(" – ") || `#${row.order.id}`;
          return <ProjectOverviewLink auftragId={row.order.id} label={label} />;
        },
      },
      {
        key: "kunde",
        header: "Kunde",
        sortable: true,
        sortValue: (row) => row.order.kunde || "",
        render: (row) => row.order.kunde || "–",
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        sortValue: (row) => row.order.project_completed,
        render: (row) => (row.order.project_completed ? "Abgeschlossen" : "Offen"),
      },
      {
        key: "planned",
        header: "Soll",
        sortable: true,
        sortValue: (row) => row.entry.plannedHours ?? -1,
        render: (row) => formatHours(row.entry.plannedHours),
      },
      {
        key: "actual",
        header: "Ist",
        sortable: true,
        sortValue: (row) => row.entry.actualHours ?? -1,
        render: (row) => formatHours(row.entry.actualHours),
      },
      {
        key: "usage",
        header: "Verbrauch",
        sortable: true,
        sortValue: (row) =>
          Number.isFinite(row.entry.percentRaw) ? Number(row.entry.percentRaw) : -1,
        render: (row) =>
          row.entry.plannedHours != null && row.entry.plannedHours > 0 ? (
            <HoursUsageCell entry={row.entry} />
          ) : (
            <span className="text-label-secondary">Keine Kalkulation</span>
          ),
      },
    ],
    [],
  );

  const isCurrentYear = year === currentYear();

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="error" title="Festpreis-Stundenverbrauch konnte nicht geladen werden">
          {error}
        </Alert>
      )}

      <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-separator text-lg text-label transition hover:border-accent hover:text-accent"
            aria-label="Vorheriges Jahr"
            onClick={() => setYear((current) => shiftYear(current, -1))}
          >
            ‹
          </button>
          <div className="min-w-[5rem] text-center text-base font-semibold text-label">{year}</div>
          <button
            type="button"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-separator text-lg text-label transition hover:border-accent hover:text-accent"
            aria-label="Nächstes Jahr"
            onClick={() => setYear((current) => shiftYear(current, 1))}
          >
            ›
          </button>
          {!isCurrentYear ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-8"
              onClick={() => setYear(currentYear())}
            >
              Aktuelles Jahr
            </Button>
          ) : null}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <TextInput
            label="Suche"
            placeholder="Auftrag, Kunde, Ort …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="flex flex-col justify-end gap-2">
            <Checkbox
              label="Nur mit Kalkulationsstunden"
              checked={onlyWithPlannedHours}
              onChange={(e) => setOnlyWithPlannedHours(e.target.checked)}
            />
            <Checkbox
              label="Nur kritische Projekte (≥ 80 %)"
              checked={onlyAtRisk}
              onChange={(e) => setOnlyAtRisk(e.target.checked)}
            />
          </div>
          <div className="flex items-end justify-start lg:justify-end">
            <Button variant="secondary" size="sm" onClick={() => void load()} loading={loading}>
              Neu laden
            </Button>
          </div>
        </div>
        <p className="mt-3 text-xs text-label-secondary">
          Alle Festpreis-Projekte des Jahres {year} (offen und abgeschlossen). Ampel nur bei
          hinterlegten Kalkulationsstunden. Grün &lt; 80 %, Gelb 80–100 %, Rot &gt; 100 %.
        </p>
      </section>

      {loading && rows === null ? (
        <div className="flex items-center justify-center py-16 text-label-secondary">
          <Spinner />
          <span className="ml-2 text-sm">Festpreis-Projekte werden geladen …</span>
        </div>
      ) : (
        <>
          <section className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Festpreis-Projekte ({year})</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-label">{summary.total}</p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Mit Kalkulation</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-label">
                {summary.withPlanned}
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Im Plan</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-label">{summary.ok}</p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Kritisch</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-label">{summary.warn}</p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Überschritten</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-label">{summary.over}</p>
            </div>
          </section>

          <section className="text-sm text-label-secondary">
            {filtered.length
              ? `${filtered.length} Projekte`
              : `Keine Festpreis-Projekte für ${year} gefunden.`}
          </section>

          <Table
            columns={columns}
            rows={filtered}
            rowKey={(row) => String(row.order.id)}
            emptyText={`Keine Festpreis-Projekte für ${year} gefunden.`}
            rowClassName={(row) => {
              const light = getHoursUsageTrafficLight(row.entry.percentRaw);
              return light ? `eh-festpreis-row--${light}` : "";
            }}
          />
        </>
      )}
    </div>
  );
}
